import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { campaignKeys, useFindContactsBatch } from '@discovr/features';
import type { OutreachOperation } from '@discovr/types';
import { findContactsErrorCopy, findContactsStartedCopy, findContactsSummary, findContactsToastKind } from './findContactsCopy';
import { codeOf } from './sendLabels';

export interface FindContactsFlow {
  operationId: string | null;
  busy: boolean;
  /** `alreadyHaveEmail` only feeds the started toast. */
  start: (entryIds: string[], alreadyHaveEmail: number) => Promise<void>;
  onDone: (operation: OutreachOperation) => void;
  onError: () => void;
}

/** The bulk bar's Find contacts: one find_contacts operation at a time. */
export function useFindContactsFlow(campaignId: string): FindContactsFlow {
  const queryClient = useQueryClient();
  const findContactsBatch = useFindContactsBatch(campaignId);
  const [operationId, setOperationId] = useState<string | null>(null);
  const busy = findContactsBatch.isPending || operationId !== null;
  // Refs, not state: a second click can land before the first one's re-render.
  const startingRef = useRef(false);
  const runningRef = useRef(false);

  // An empty entry_ids means "every contactless row" server-side, so only a non-empty list is sent.
  const start = async (entryIds: string[], alreadyHaveEmail: number) => {
    if (entryIds.length === 0 || busy || startingRef.current || runningRef.current) return;
    startingRef.current = true;
    try {
      const { operationId: id } = await findContactsBatch.mutateAsync(entryIds);
      runningRef.current = true;
      setOperationId(id);
      toast.success(findContactsStartedCopy(entryIds.length, alreadyHaveEmail));
    } catch (e) {
      toast.error(findContactsErrorCopy(codeOf(e)));
    } finally {
      startingRef.current = false;
    }
  };

  const onDone = (operation: OutreachOperation) => {
    runningRef.current = false;
    setOperationId(null);
    const kind = findContactsToastKind(operation);
    if (kind === 'error') toast.error(findContactsSummary(operation));
    else toast[kind](findContactsSummary(operation), { description: 'Emails appear on each row as they are found.' });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  };

  const onError = () => {
    runningRef.current = false;
    setOperationId(null);
    toast.error("Couldn't read this batch's progress. Try again.");
  };

  return { operationId, busy, start, onDone, onError };
}
